import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import SearchResultEntity from "../../../../domain/entities/SearchResultEntity";
import di from "../../../../DependencyInjection";
import SearchQrsUseCase, { SearchQrsUseCaseName } from "../../../../domain/use_cases/qr/SearchQrsUseCase";
import DisableQrUseCase, { DisableQrUseCaseName } from "../../../../domain/use_cases/qr/DisableQrUseCase";
import CreateQrsComponent from "./form/create_qrs.component";
import QrBatchEntity from "../../../../domain/entities/QrBatchEntity";
import { AutoCompleteItem } from "../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import StablishmentEntity from "../../../../domain/entities/StablishmentEntity";
import SearchStablishmentsUseCase, { SearchStablishmentsUseCaseName } from "../../../../domain/use_cases/stablishment/SearchStablishmentsUseCase";
import GetStablishmentByIdUseCase, { GetStablishmentByIdUseCaseName } from "../../../../domain/use_cases/stablishment/GetStablishmentByIdUseCase";
import ReDownloadQrBatchUseCase, { ReDownloadQrBatchUseCaseName } from "../../../../domain/use_cases/qr/ReDownloadQrBathUseCase";

const useQrListViewModel = () => {
    const [searchParams] = useSearchParams();
    const stablishmentQuery = searchParams.get("stablishment");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");

    const { i18n } = useContext(LocalizationContext);
    const { addToast, openModalCustom, closeModalCustom, openModalDelete, closeModalDelete } = useContext(ModalsContext);
    const formInfo = useForm();
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = formInfo;
    const [result, setResult] = useState<SearchResultEntity<QrBatchEntity> | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    const itemsPerPage = 20;
    let _timerTap: any;

    const _onSearchStablishments = async (query: string): Promise<AutoCompleteItem<StablishmentEntity>[]> => {
        const response = await di.get<SearchStablishmentsUseCase>(SearchStablishmentsUseCaseName).call({
            query, page: 1,
            itemsPerPage: 20,
        });
        if (isLeft(response)) return [];
        const parsed = response.right.data.map((item) => {
            return {
                label: `${item.name} - ${item.address}`,
                id: item.id,
                aditionalValue: item,
            };
        });
        return parsed;
    };

    // Función para cargar el stablishment por ID cuando viene de la URL
    const _loadStablishment = async () => {
        if (stablishmentQuery && getValues()[KeyWordLocalization.QrListPageStablishment] == "") {
            const response = await di.get<GetStablishmentByIdUseCase>(GetStablishmentByIdUseCaseName).call(stablishmentQuery);
            if (isLeft(response)) return;
            const parsed: AutoCompleteItem<StablishmentEntity> = {
                label: `${response.right.name} - ${response.right.address}`,
                id: response.right.id,
                aditionalValue: response.right,
            };
            setValue(KeyWordLocalization.QrListPageStablishment, [parsed]);
        }
    };

    const _searchQrs = async () => {
        const data = getValues();
        if(stablishmentQuery == null || stablishmentQuery == undefined) {
            setLoading(false);
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        clearTimeout(_timerTap);
        setResult(undefined);
        setLoading(true);
        const response = await di.get<SearchQrsUseCase>(SearchQrsUseCaseName).call({
            // query: data[KeyWordLocalization.QrListPageQuery], TODO replace with 
            stablishmentId: data[KeyWordLocalization.QrListPageStablishment]?.[0]?.id,
            page: data[KeyWordLocalization.QrListCurrentPage] ?? 1,
            itemsPerPage
        });
        setLoading(false);
        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    const _handleSearch = (data: any) => {
        const search = data[KeyWordLocalization.QrListPageQuery];
        const page = data[KeyWordLocalization.QrListCurrentPage];
        const status = data[KeyWordLocalization.QrListPageStatusQuery];
        const stablishment = data[KeyWordLocalization.QrListPageStablishment]?.[0]?.id;
        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if (status && status.length > 0) query += `status=${status}&`;
        if (page) query += `page=${page}&`;
        if (stablishment) query += `stablishment=${stablishment}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.qr_list.relativePath + query);
    };

    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = async () => {
        setValue(KeyWordLocalization.QrListCurrentPage, pageQuery);
        await _loadStablishment();
        _searchQrs();
    };

    const _handleOnChangePage = (page: number) => {
        setValue(KeyWordLocalization.QrListCurrentPage, page);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleOnClickDisableQr = async (qr: QrBatchEntity) => {
        const disableQr = async () => {
            const response = await di.get<DisableQrUseCase>(DisableQrUseCaseName).call(qr.id);
            if (isLeft(response)) {
                addToast(i18n("Error al deshabilitar QRs"), "error", undefined);
                return;
            }
            closeModalDelete();
            addToast(i18n("QRs deshabilitado exitosamente"), "success", undefined);
            _searchQrs();
        };

        openModalDelete(
            i18n("Deshabilitar QRs"),
            i18n(`¿Estás seguro de que deseas deshabilitar los QRs del id ${qr.id}? Esta acción no se puede deshacer.`),
            disableQr
        );
    };

    const _handleOnCreateQrs = () => {
        const _onSuccess = () => {
            closeModalCustom();
            _searchQrs();
        };
        openModalCustom(
            "lg",
            i18n("Crear QRs"),
            <CreateQrsComponent onCreated={_onSuccess} />
        );
    };

    const _handleDownloadQrBatch = async (qr: QrBatchEntity) => {
        setLoadingDownload(true);
        const response = await di.get<ReDownloadQrBatchUseCase>(ReDownloadQrBatchUseCaseName).call({
            stablishmentId: stablishmentQuery!,
            zipToken: qr.id,
        });
        if (isLeft(response)) {
            addToast(i18n("Error al descargar QRs"), "error", undefined);
        } else {
            addToast(i18n("Descarga iniciada"), "success", undefined);
        }
        setLoadingDownload(false);
    };

    // Función para manejar cambio de stablishment del AutoComplete
    const handleChangeStablishment = (item: AutoCompleteItem<StablishmentEntity>) => {
        setValue(KeyWordLocalization.QrListPageStablishment, item ? [item] : []);
        _handleChange();
    };

    useEffect(() => {
        _handleLoad();
    }, [pageQuery, stablishmentQuery]);

    return {
        formInfo,
        result,
        loadingDownload,
        i18n,
        _handleSearch,
        _handleChangeTextSearch,
        _handleChange,
        _handleOnChangePage,
        _handleOnClickDisableQr,
        _handleOnCreateQrs,
        _handleDownloadQrBatch,
        _onSearchStablishments,
        handleChangeStablishment,
        loading,
    };
};

export default useQrListViewModel;